import {IconProp} from "@fortawesome/fontawesome-svg-core";
import {faBan, faPlus} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import React, {useCallback, useEffect, useState} from 'react';
import {Button} from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import ConfirmCancelRegistrationModal from "../Modal/ConfirmCancelRegistrationModal";
import PaymentCollectedModal from '../Modal/PaymentCollectedModal';
import PaymentModal from '../Modal/PaymentModal';
import RegistrationCancelled from "../Modal/RegistrationCancelled";
import RegistrationConfirmed from "../Modal/RegistrationConfirmed";
import {useNotice} from "../Providers/NoticeProvider";
import {
    ProgramCourse,
    ProgramsProviderContext,
    STATUS_WAITLIST,
    STATUS_REGISTERED,
    STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT,
    STATUS_REGISTERED_WITH_CREDITS,
    STATUS_WAITLISTED, STATUS_CLOSED_NO_WAITLIST, STATUS_CLOSED
} from "../Providers/ProgramsProvider";
import CourseQuestionModal from "../Modal/CourseQuestionModal";

type Props = {
    course: ProgramCourse;
    programName: string;
};

const RegisterButton = ({course, programName}: Props) => {
    const [notice] = useNotice();
    const [loading, setLoading] = useState<boolean>(false);
    const [showConfirmCancelRegistrationModal, setShowConfirmCancelRegistrationModal] = useState<boolean>(false);
    const [showRegistrationCancelledModal, setShowRegistrationCancelledModal] = useState<boolean>(false);
    const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
    const [showCourseQuestionModal, setShowCourseQuestionModal] = useState<boolean>(false);
    const [courseHasCreditsApplied, setCourseHasCreditsApplied] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [, performRegistrationAction, , , , ,setErrorMessage, refreshRegistrations] = React.useContext(ProgramsProviderContext);
    const [confirmationId, setConfirmationId] = useState('');

    let buttonText: string = 'Register';
    let buttonTextAction: string = 'Registering';
    let buttonIcon: IconProp = faPlus;

    const performActionBasedOnCourseStatus = async (course : ProgramCourse) => {
        const clickStatus = course.status;
        if (course.requiresPayment && clickStatus === 'waitlisted') {
            setShowConfirmCancelRegistrationModal(true);
        } else if (course.requiresPayment && clickStatus === 'registered-with-credits-pending-payment') {
            setCourseHasCreditsApplied(true);
            setShowPaymentModal(true);
        } else if (performRegistrationAction) {
            try {
                setLoading(true);
                const registered = await Promise.race([
                    performRegistrationAction(course, programName),
                    new Promise((resolve, reject) => {
                        setTimeout(reject, 30 * 1000)// 30 second timeout.  if we didn't resolve by now just clear the loading
                    })
                ]);
                setLoading(false);

                if (registered && !!course.requiresPayment && clickStatus !== 'waitlisted') {
                    setShowPaymentModal(true);
                }
            } catch (e) {
                console.error('rejected registering', e);
                setErrorMessage('The system is currently experiencing higher than usual demand. Please try your request again.')//GLENR-184
                setLoading(false);
            }
        }
    }

    if (course.paymentDue) {
        buttonText = 'Update Payment';
        buttonTextAction = 'Switching';
        buttonIcon = faPlus;
    } else {

        switch (course.status) {
            case STATUS_CLOSED_NO_WAITLIST:
                buttonText = 'Course Full';
                buttonIcon = faPlus;
                break;
            case STATUS_WAITLIST:
                buttonText = 'Course Is Full - Click to Add to Waitlist.';
                buttonTextAction = 'Adding';
                buttonIcon = faPlus;
                break;
            case STATUS_WAITLISTED:
                buttonText = 'Remove From Waitlist';
                buttonTextAction = 'Removing';
                buttonIcon = faBan;
                break;
            case STATUS_REGISTERED:
                if (showPaymentModal) {
                    buttonText = 'You are Registered pending payment';
                    buttonTextAction = 'Cancelling';
                } else {
                    buttonText = 'You are Registered - Click to Cancel Registration';
                    buttonTextAction = 'Cancelling';
                }
                buttonIcon = faBan;
                break;
            case STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT:
                buttonText = 'You are registered - Click to add payment';
                buttonTextAction = 'Registering';
                buttonIcon = faPlus;
                break;
            case STATUS_REGISTERED_WITH_CREDITS:
                buttonText = 'You are registered with credits applied - Click to Cancel';
                buttonTextAction = 'Cancelling';
                buttonIcon = faBan;
                break;
            default:
                break;
        }
    }

    const handleCloseCancelRegistration = useCallback(async (): Promise<void> => {
        setShowConfirmCancelRegistrationModal(false);
    }, [setShowConfirmCancelRegistrationModal]);

    const handleButtonClick = async () => {
        setShowConfirmCancelRegistrationModal(false);

        if (course.courseQuestions) {
            setShowCourseQuestionModal(true);
        }

        if (!course.courseQuestions) {
            await performActionBasedOnCourseStatus(course);
        }
    };

    const handleCancelRegistration = async () => {
        setShowConfirmCancelRegistrationModal(false);

        if (performRegistrationAction) {
            setLoading(true);
            await performRegistrationAction(course, programName);
            setLoading(false);
        }
    };

    const handleClosePaymentModal = useCallback(async (showCancelModal?: boolean, showConfirmModal?: boolean): Promise<void> => {
        setShowPaymentModal(false);

        if (showCancelModal) {
            setShowRegistrationCancelledModal(true);
        }

        if (showConfirmModal) {
            setShowConfirmationModal(true);
        }
    }, [setShowPaymentModal]);

    useEffect(() => {
        const registrationCourse = localStorage.getItem('registration-course-' + course.courseSelectionUUID);

        if (registrationCourse) {
            const registrationCourseJson = JSON.parse(registrationCourse);
            const timeout = Date.parse(registrationCourseJson.timeout);

            if (Date.now() < timeout) {
                setShowPaymentModal(true);
            }
        }
    }, [course.courseSelectionUUID])

    return (
        <>
            <Button
                variant="outline-primary"
                type="submit"
                className="d-flex pt-2 flex-row align-content-center"
                onClick={async () => {
                    if (course.status !== STATUS_REGISTERED && course.status !== STATUS_REGISTERED_WITH_CREDITS) {
                        setShowConfirmCancelRegistrationModal(false);
                        await handleButtonClick();
                    } else if (course.paymentDue) {
                        setShowConfirmCancelRegistrationModal(false);
                        setShowPaymentModal(true);
                    } else {
                        setShowConfirmCancelRegistrationModal(true);
                    }
                }}
                disabled={loading || [STATUS_CLOSED_NO_WAITLIST].includes(course.status)}
            >
                {loading ? (
                    <React.Fragment>
                        <Spinner animation="border" size="sm" className="mt-1"/>
                        &nbsp; {buttonTextAction}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {course.status !== STATUS_CLOSED_NO_WAITLIST && <FontAwesomeIcon icon={buttonIcon} size="sm" className="mt-1 mr-1"/>}
                        {buttonText}
                    </React.Fragment>
                )}
            </Button>
            <ConfirmCancelRegistrationModal
                show={!course.requiresPayment && showConfirmCancelRegistrationModal}
                message={notice.cancelPaymentRegistration}
                handleClose={handleCloseCancelRegistration}
                handleCancelRegistration={handleCancelRegistration}
            />
            <PaymentCollectedModal
                course={course}
                show={!!course.requiresPayment && showConfirmCancelRegistrationModal}
                handlePaymentCollectedModal={handleCloseCancelRegistration}
            />
            <PaymentModal
                course={course}
                programName={programName}
                show={showPaymentModal}
                handleClose={handleClosePaymentModal}
                setConfirmationId={setConfirmationId}
                switchPayment={course.paymentDue}
                courseHasCreditsApplied={courseHasCreditsApplied}
            />
            <RegistrationCancelled
                show={showRegistrationCancelledModal}
                handleClose={() => setShowRegistrationCancelledModal(false)}
            />
            <RegistrationConfirmed
                show={showConfirmationModal}
                handleClose={() => {
                        setShowConfirmationModal(false);
                        void refreshRegistrations();
                    }
                }
                confirmationNumber={confirmationId}
                isWaitlist={course.status === 'waitlisted'}
                confirmationMessage={notice.registrationPaymentConfirmed}
                waitlistMessage={notice.waitlistPaymentConfirmed}
                isPreRegistered={course.status === 'registered-with-credits-pending-payment' || course.status === 'registered-with-credits'}
            />
            {course.courseQuestions && <CourseQuestionModal
                show={showCourseQuestionModal}
                handleClose={() => setShowCourseQuestionModal(false)}
                course={course}
                performActionBasedOnCourseStatus={performActionBasedOnCourseStatus}
            />}
        </>
    );
};

export default RegisterButton;
